import { css } from "@compiled/react";
import { FormattedMessage, useIntl } from "react-intl";

import { media } from "@atlaskit/primitives/responsive";
import { token } from "@atlaskit/tokens";

import { Image } from "@/components/wpl/image/src";
import { Quote } from "@/components/wpl/tab-group/src";
import { Link } from "@/libraries/design/components";
import { DEFAULT_VALUES, useLocalizedHref } from "@/libraries/web-framework";
import { DataEventProps, Row } from "@/libraries/wpl/foundations/src";

import { getWorkflowSectionImage } from "../../../localized-images";

const sectionStyles = css({
    "alignItems": "center",
    "color": "#ffffff",
    "display": "flex",
    "flexDirection": "column",

    "backgroundImage": `linear-gradient(to right, transparent 50%, #091e42 50%),
        linear-gradient(to bottom, #091e42 0%, #172b4d 100%)`,
    "backgroundRepeat": "no-repeat",

    [media.below.sm]: {
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        ".workflow-body": {
            display: "flex",
            flexDirection: "row"
        },
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        ".icon-gradient": {
            flex: "1"
        },
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        ".workflow-container": {
            flex: "8"
        },

        "backgroundImage":
            "linear-gradient(to bottom, #091e42 0%, #172b4d 100%)",
        "backgroundPosition": "center",
        "backgroundRepeat": "no-repeat",
        "backgroundSize": "cover"
    },

    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".template-items-container": {
        width: "80%"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".quote-image": {
        width: "45px"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".quote-blurb": {
        fontSize: "1.25rem",
        lineHeight: "1.2"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".quote-name": {
        fontSize: "0.875rem",
        lineHeight: "1.25",
        width: "75%"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".quote-company": {
        fontSize: "0.75rem !important",
        lineHeight: "2",
        width: "75%"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".workflow-paragraph": {
        textAlign: "center"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".workflow-container": {
        [media.above.sm]: {
            display: "flex"
        }
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".workflow-center-left": {
        display: "flex",
        alignItems: "center",
        flexDirection: "column",
        paddingInlineStart: "32px",

        [media.above.sm]: {
            paddingBlockEnd: `${token("space.400")}`,
            paddingInlineEnd: "10%",
            paddingInlineStart: 0
        }
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".workflow-center-right": {
        display: "flex",
        alignItems: "center",
        flexDirection: "column",
        paddingBlockEnd: `${token("space.400")}`,
        paddingInlineStart: "32px",

        [media.above.sm]: {
            paddingInlineStart: "10%"
        }
    }
});

const h3Styles = css({
    textAlign: "center",
    margin: `${token("space.400")} 0 0 0`,
    fontWeight: "normal",
    [media.above.sm]: {
        fontSize: "1.5rem"
    },
    [media.below.sm]: {
        fontSize: "1.3rem"
    }
});

export const LocalizedDiagramImage = () => {
    const intl = useIntl();
    const localizedImageSrc = getWorkflowSectionImage(intl.locale);

    return (
        <Image
            src={localizedImageSrc}
            alt="Diagram showcasing an example workflow using Jira Software"
        />
    );
};

const WorkflowSection = () => {
    const intl = useIntl();

    return (
        <section css={sectionStyles}>
            <h2
                css={css({
                    textAlign: "center",
                    fontWeight: "normal",
                    fontSize: "2.25rem",
                    [media.only.sm]: {
                        fontSize: "2rem"
                    },
                    [media.below.sm]: {
                        fontSize: "1.5rem",
                        margin: "0"
                    }
                })}
            >
                <FormattedMessage
                    id="JiraSoftwarePageTemplate.workflowSection.header"
                    defaultMessage="Easier than ever"
                    values={DEFAULT_VALUES}
                />
            </h2>
            <Row>
                <div className="workflow-body">
                    <div className="icon-gradient">
                        <IconGradient />
                    </div>
                    <div className="workflow-container">
                        <div className="workflow-center-left">
                            <h3 css={h3Styles}>
                                <FormattedMessage
                                    id="JiraSoftwarePageTemplate.workflowSection.templatesHeader"
                                    defaultMessage="Templates give you a head start"
                                    values={DEFAULT_VALUES}
                                />
                            </h3>
                            <p className="workflow-paragraph">
                                <FormattedMessage
                                    id="JiraSoftwarePageTemplate.workflowSection.templatesSubheader"
                                    defaultMessage="Get started with ready-made templates."
                                    values={DEFAULT_VALUES}
                                />
                            </p>
                            <div className="template-items-container">
                                <AgileTemplateItem />
                                <KanbanTemplateItem />
                                <BugTemplateItem />
                                <DevopsTemplateItem />
                            </div>
                            <div
                                css={css({
                                    borderRadius: "0px 150px 150px 0px",
                                    marginInlineStart: "5%"
                                })}
                            >
                                <Quote
                                    image={<QuotationImage />}
                                    quote={
                                        intl.formatMessage({
                                            id: "JiraSoftwarePageTemplate.workflowSection.quote",
                                            defaultMessage:
                                                "You can decide how you are working as a team first and then let the software work around you."
                                        }) + '"'
                                    }
                                    name={intl.formatMessage({
                                        id: "JiraSoftwarePageTemplate.workflowSection.name",
                                        defaultMessage: "Maria Bonorchis"
                                    })}
                                    company={intl.formatMessage({
                                        id: "JiraSoftwarePageTemplate.workflowSection.company",
                                        defaultMessage:
                                            "Agile Delivery Manager, Cancer Research UK"
                                    })}
                                    colors={{
                                        defaultBG: "#42526e",
                                        defaultText: "#dfe1e6",
                                        activeBG: "#57d9a3",
                                        activeText: "#091e42",
                                        hoverBG: "#172b4d",
                                        hoverText: "#dfe1e6",
                                        quoteColor: "#0065ff"
                                    }}
                                />
                            </div>
                        </div>
                        <div className="workflow-center-right">
                            <h3 css={h3Styles}>
                                <FormattedMessage
                                    id="JiraSoftwarePageTemplate.workflowSection.customizeHeader"
                                    defaultMessage="Then customize as you grow"
                                    values={DEFAULT_VALUES}
                                />
                            </h3>
                            <p className="workflow-paragraph">
                                <FormattedMessage
                                    id="JiraSoftwarePageTemplate.workflowSection.customizeSubheader"
                                    defaultMessage="Jira adapts to the way you work, not the other way around. Start simple, customize as you go."
                                    values={DEFAULT_VALUES}
                                />
                            </p>
                            <LocalizedDiagramImage />
                        </div>
                    </div>
                </div>
            </Row>
        </section>
    );
};

export const QuotationImage = () => {
    const intl = useIntl();

    return (
        <Image
            className="quote-image"
            src="https://dam-cdn.atl.orangelogic.com/AssetLink/41knb7g4sf3cg6sj54o2uubv0r3r3n33.svg"
            alt={intl.formatMessage({
                id: "JiraSoftwarePageTemplate.workflowSection.quoteImage",
                defaultMessage: "Quotation mark"
            })}
        />
    );
};

const iconGradientStyles = css({
    "display": "flex",
    "justifyContent": "space-between",
    "maxWidth": "768px",

    "backgroundPosition": "center center",
    "backgroundRepeat": "no-repeat",

    [media.below.sm]: {
        flexDirection: "column",
        backgroundImage: "linear-gradient(to bottom, #0065ff, #79f2c0)",
        backgroundSize: "5px 90%",
        paddingBlockStart: "25%",
        paddingBlockEnd: "25%",
        marginBlockStart: "50%",
        height: "57%"
    },
    [media.above.sm]: {
        flexDirection: "row",
        backgroundImage: "linear-gradient(to right, #0065ff, #79f2c0)",
        backgroundSize: "80% 5px",
        paddingInlineStart: "2%",
        paddingInlineEnd: "2%",
        marginInlineStart: "15%",
        marginInlineEnd: "15%",
        width: "70%"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".workflow-icon": {
        width: "56px"
    }
});

export const AgileTemplateItem = () => {
    const intl = useIntl();

    return (
        <TemplateItem
            headerText={intl.formatMessage({
                id: "JiraSoftwarePageTemplate.workflowSection.scrum",
                defaultMessage: "Scrum"
            })}
            image="https://dam-cdn.atl.orangelogic.com/AssetLink/643n66r557l1i8psu0j0x23qpm6iwou8.svg"
            imageAltText={intl.formatMessage({
                id: "JiraSoftwarePageTemplate.workflowSection.scrumImageAltText",
                defaultMessage: "Agile illustration"
            })}
            href={useLocalizedHref("/software/jira/templates/scrum")}
        />
    );
};

export const KanbanTemplateItem = () => {
    const intl = useIntl();

    return (
        <TemplateItem
            headerText={intl.formatMessage({
                id: "JiraSoftwarePageTemplate.workflowSection.kanban",
                defaultMessage: "Kanban"
            })}
            image="https://dam-cdn.atl.orangelogic.com/AssetLink/0det02q2xy1le35o8c17l73a4l2i5cc3.svg"
            imageAltText={intl.formatMessage({
                id: "JiraSoftwarePageTemplate.workflowSection.kanbanImageAltText",
                defaultMessage: "Kanban illustration"
            })}
            href={useLocalizedHref("/software/jira/templates/kanban")}
        />
    );
};

export const BugTemplateItem = () => {
    const intl = useIntl();

    return (
        <TemplateItem
            headerText={intl.formatMessage({
                id: "JiraSoftwarePageTemplate.workflowSection.bugTracking",
                defaultMessage: "Bug tracking"
            })}
            image="https://dam-cdn.atl.orangelogic.com/AssetLink/qom8s2168ye5a741003k5m28v8r80g57.svg"
            imageAltText={intl.formatMessage({
                id: "JiraSoftwarePageTemplate.workflowSection.bugTrackingImageAltText",
                defaultMessage: "Bug illustration"
            })}
            href={useLocalizedHref("/software/jira/templates/bug-tracking")}
        />
    );
};

export const DevopsTemplateItem = () => {
    const intl = useIntl();

    return (
        <TemplateItem
            headerText={intl.formatMessage({
                id: "JiraSoftwarePageTemplate.workflowSection.devops",
                defaultMessage: "DevOps"
            })}
            image="https://dam-cdn.atl.orangelogic.com/AssetLink/a3sp26uogt07ms5yk75s00e817f0bc84.svg"
            imageAltText={intl.formatMessage({
                id: "JiraSoftwarePageTemplate.workflowSection.devopsImageAltText",
                defaultMessage: "Devops illustration"
            })}
            href={useLocalizedHref("/software/jira/templates/devops")}
        />
    );
};

export const TemplatesIcon = () => {
    const intl = useIntl();

    return (
        <Image
            className="workflow-icon"
            src="https://dam-cdn.atl.orangelogic.com/AssetLink/5vtd6c8c862vqv281l8rhxtq1hk4s273.svg"
            alt={intl.formatMessage({
                id: "JiraSoftwarePageTemplate.workflowSection.templatesIcon",
                defaultMessage: "Templates icon"
            })}
        />
    );
};
export const WorkflowIcon = () => {
    const intl = useIntl();

    return (
        <Image
            className="workflow-icon"
            src="https://dam-cdn.atl.orangelogic.com/AssetLink/ukex56bd8hjxoa63505tiq5s671s7qgc.svg"
            alt={intl.formatMessage({
                id: "JiraSoftwarePageTemplate.workflowSection.workflowIcon",
                defaultMessage: "Workflow icon"
            })}
        />
    );
};

const IconGradient = () => {
    return (
        <div css={iconGradientStyles}>
            <TemplatesIcon />
            <WorkflowIcon />
        </div>
    );
};

const templateStyles = css({
    "display": "flex",
    "justify": "start",
    "noWrap": "true",
    "margin": `${token("space.400")} 0`,
    "width": "100%",

    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".template-header": {
        paddingBlockStart: `${token("space.100")}`,
        paddingBlockEnd: `${token("space.100")}`,
        fontWeight: "500",
        fontSize: "1.25rem",
        fontFamily: "var(--bxp-charlie-display-font-family)"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".template-image-container": {
        alignItems: "center",
        backgroundColor: "#253858",
        display: "flex",
        minHeight: `${token("space.800")}`,
        justifyContent: "center",
        padding: `${token("space.200")}`,
        width: "110px",
        noWrap: "true"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".template-link-container": {
        "alignContent": "stretch",
        "backgroundColor": "#172b4d",
        "display": "flex",
        "flexDirection": "column",
        "justifyContent": "center",
        "paddingInlineStart": `${token("space.200")}`,
        "paddingBlockStart": `${token("space.100")}`,
        "paddingBlockEnd": `${token("space.200")}`,
        "width": "100%",
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        "h4": {
            margin: "0 0 14px"
        },
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        "a,a:focus,a:visited,a:hover": {
            color: "#fafbfc",
            // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
            svg: {
                transition: "transform 0.25s ease-in-out",
                // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
                path: {
                    fill: "#fafbfc"
                }
            }
        },
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        "a:hover": {
            textDecoration: "underline",
            // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
            svg: {
                transform: `translateX(${token("space.100")})`
            }
        }
    }
});

const TemplateItem = ({
    headerText,
    image,
    imageAltText,
    href
}: {
    headerText: string;
    image: string;
    imageAltText: string;
    href: string;
}) => {
    const intl = useIntl();

    const dataEventProps: DataEventProps = {
        "data-event-component": "linkButton",
        "data-event-container": "workflowSection",
        "data-event": "clicked",
        "data-product-key": "jiraSoftware"
    };

    return (
        <div css={templateStyles}>
            <div className="template-image-container">
                <Image src={image} alt={imageAltText} />
            </div>
            <div className="template-link-container">
                <div className="template-header">{headerText}</div>
                <Link
                    text={intl.formatMessage({
                        id: "JiraSoftwarePageTemplate.workflowSection.learnMore",
                        defaultMessage: "Learn more"
                    })}
                    href={href}
                    bgColor="bgBlackBlue"
                    dataEventProps={dataEventProps}
                />
            </div>
        </div>
    );
};

WorkflowSection.displayName = "WorkflowSection";

export default WorkflowSection;
